import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Add, Visibility, QrCode, Image, Close,
  Link as LinkIcon, CameraAlt, Search, PictureAsPdf,
  Code, Person, BarChart, Note, People, Bolt, Rocket, Warning, Timer
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { me } from '../../redux/features/authenSlice';
import useAuth from '../../hooks/useAuth';
import useLogout from '../../hooks/logout';
import { formatDateHumanReadable } from '../../utils/date';

// Custom gradient colors for cards
const cardGradients = [
  'from-blue-500 to-blue-600',
  'from-emerald-500 to-emerald-600',
  'from-violet-500 to-violet-600',
  'from-rose-500 to-rose-600',
  'from-amber-500 to-amber-600',
  'from-cyan-500 to-cyan-600',
  'from-indigo-500 to-indigo-600',
  'from-teal-500 to-teal-600',
  'from-purple-500 to-purple-600'
];

// Counter animation hook (optimized)
const useCountUp = (end, duration = 2000, delay = 0) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!hasStarted) {
      const timer = setTimeout(() => {
        setHasStarted(true);
      }, delay);
      return () => clearTimeout(timer);
    }

    if (hasStarted && end > 0) {
      let startTime;
      let animationFrame;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeOutQuart * end));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [end, duration, delay, hasStarted]);

  return count;
};

const DashboardCard = ({
  title,
  value,
  total,
  icon,
  index
}) => {
  const animatedValue = useCountUp(value, 2000, index * 100);
  const gradient = cardGradients[index % cardGradients.length];

  const percentage = useMemo(() => {
    if (total <= 0) return 0;
    return Math.min((value / total) * 100, 100);
  }, [value, total]);

  const progressWidth = useMemo(() => {
    if (value > 0 && percentage < 1) return 1;
    return percentage;
  }, [percentage, value]);

  const displayTotal = useMemo(() => {
    return total <= 0 ? '∞' : total.toLocaleString();
  }, [total]);

  const displayValue = useMemo(() => {
    return animatedValue.toLocaleString();
  }, [animatedValue]);

  const displayPercentage = useMemo(() => {
    if (total === 0) return "0";
    const percentage = (value / total) * 100;
    return percentage.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }, [value, total]);

  const isNearLimit = percentage > 80;
  const isAtLimit = percentage >= 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg group overflow-hidden relative"
      whileHover={{ y: -5 }}
    >
      {/* Gradient accent */}
      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${gradient}`}></div>

      <div className="flex justify-between items-start mb-5">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${gradient} shadow-md`}>
          {React.cloneElement(icon, { className: "text-white text-xl" })}
        </div>
        <div className="text-right">
          <div className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full inline-flex items-center gap-1">
            {total > 0 ? (
              <>
                <span>{displayPercentage}%</span>
                <div className="w-8 h-1 rounded-full bg-gray-300 overflow-hidden">
                  <div
                    className={`h-full ${isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-400' : 'bg-gray-500'}`}
                    style={{ width: `${progressWidth}%` }}
                  />
                </div>
              </>
            ) : (
              <span>∞</span>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-base font-medium text-gray-600 mb-2">{title}</h3>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-gray-900">{displayValue}</span>
          <span className="text-sm font-medium text-gray-500">/ {displayTotal}</span>
        </div>
      </div>

      {/* Status Indicator */}
      {isAtLimit || isNearLimit ? (
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-medium">
            {isAtLimit ? (
              <span className="text-red-600 flex items-center gap-1">
                <Warning className="text-xs" /> Limit Reached
              </span>
            ) : isNearLimit ? (
              <span className="text-amber-600 flex items-center gap-1">
                <Warning className="text-xs" /> Near Limit
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Progress Bar */}
      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progressWidth}%` }}
          transition={{ duration: 2, delay: index * 0.1 + 0.5, ease: "easeOut" }}
          className={`h-2 rounded-full ${isAtLimit ? 'bg-red-500' :
            isNearLimit ? 'bg-amber-400' :
              `bg-gradient-to-r ${gradient}`
            }`}
        />
      </div>
    </motion.div>
  );
};

const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const { token } = useAuth();
  const { user } = useSelector((state) => state.authen) || {};
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const userData = user?.response || user || {};
  const userName = userData?.name || 'User';

  const {
    trialEndsAt,
    subscriptionStatus,
    subscriptionEndDate,
    usersLimit = 0,
    usersUsed = 0,
    shortUrlLimit = 0,
    shortUrlUsed = 0,
    screenshotsLimit = 0,
    screenshotsUsed = 0,
    webSearchLimit = 0,
    webSearchUsed = 0,
    pdfLimit = 0,
    pdfUsed = 0,
    htmlScraperLimit = 0,
    htmlScraperUsed = 0,
    bioPageLimit = 0,
    bioPageUsed = 0,
    seoScraperLimit = 0,
    seoScraperUsed = 0,
    sNotesLimit = 0,
    sNotesUsed = 0
  } = userData;

  const trialEndDate = useMemo(
    () => (trialEndsAt ? new Date(trialEndsAt) : null),
    [trialEndsAt]
  );

  const isTrialExpired = useMemo(
    () => (trialEndDate ? trialEndDate < new Date() : false),
    [trialEndDate]
  );
  const showTrial = useMemo(() => {
    if (!trialEndDate || isTrialExpired) return false;

    const warningDate = new Date(trialEndDate);
    warningDate.setDate(warningDate.getDate() - 7);


    if (new Date() >= warningDate && subscriptionStatus === null) {
      return true;
    }

    return false;
  }, [trialEndDate, subscriptionStatus]);

  const isSubscriptionEndingSoon = useMemo(() => {
    if (!subscriptionEndDate) return false;

    const subsWarningDate = new Date(subscriptionEndDate);
    subsWarningDate.setDate(subsWarningDate.getDate() - 7);

    return new Date() >= subsWarningDate &&
      subscriptionStatus !== 'ended' &&
      subscriptionStatus !== null;
  }, [subscriptionEndDate, subscriptionStatus]);

  const showSubscriptionWarning = useMemo(() => {
    return isSubscriptionEndingSoon && subscriptionStatus == 'active';
  }, [isSubscriptionEndingSoon, subscriptionStatus]);

  const closeModal = useCallback((e) => {
    if (e.target.id === 'modal-overlay') setIsModalOpen(false);
  }, []);

  useEffect(() => {
    if (!token) return;

    const controller = new AbortController();

    dispatch(me({ token, signal: controller.signal }))
      .unwrap()
      .catch((error) => {
        if (error.statusCode === 401) {
          handleLogout();
          navigate('/');
        }
      });

    return () => controller.abort();
  }, [token]);

  // Card data with MUI icons
  const cardData = useMemo(() => [
    {
      title: 'Short Links',
      value: shortUrlUsed,
      total: shortUrlLimit,
      icon: <LinkIcon />,
    },
    {
      title: 'Screenshots',
      value: screenshotsUsed,
      total: screenshotsLimit,
      icon: <CameraAlt />,
    },
    {
      title: 'Web Search',
      value: webSearchUsed,
      total: webSearchLimit,
      icon: <Search />,
    },
    {
      title: 'PDF Generator',
      value: pdfUsed,
      total: pdfLimit,
      icon: <PictureAsPdf />,
    },
    {
      title: 'HTML Scraper',
      value: htmlScraperUsed,
      total: htmlScraperLimit,
      icon: <Code />,
    },
    {
      title: 'Bio Pages',
      value: bioPageUsed,
      total: bioPageLimit,
      icon: <Person />,
    },
    {
      title: 'SEO Scraper',
      value: seoScraperUsed,
      total: seoScraperLimit,
      icon: <BarChart />,
    },
    {
      title: 'StackNotes',
      value: sNotesUsed,
      total: sNotesLimit,
      icon: <Note />,
    },
    {
      title: 'Team Users',
      value: usersUsed,
      total: usersLimit,
      icon: <People />,
    }
  ], [
    shortUrlUsed, shortUrlLimit,
    screenshotsUsed, screenshotsLimit,
    webSearchUsed, webSearchLimit,
    pdfUsed, pdfLimit,
    htmlScraperUsed, htmlScraperLimit,
    bioPageUsed, bioPageLimit,
    seoScraperUsed, seoScraperLimit,
    sNotesUsed, sNotesLimit,
    usersUsed, usersLimit
  ]);

  const modalActions = useMemo(() => [
    {
      label: 'Short Link',
      icon: <LinkIcon className="w-5 h-5" />,
      color: 'bg-blue-50 hover:bg-blue-100 text-blue-600',
      hoverColor: 'hover:shadow-blue-100',
      onClick: () => navigate('/links', { state: { isModalOpen: true } })
    },
    {
      label: 'Generate PDF',
      icon: <PictureAsPdf className="w-5 h-5" />,
      color: 'bg-green-50 hover:bg-green-100 text-green-600',
      hoverColor: 'hover:shadow-green-100',
      onClick: () => navigate('/pdfs', { state: { isModalOpen: true } })
    },
    {
      label: 'Scrape Website',
      icon: <Code className="w-5 h-5" />,
      color: 'bg-amber-50 hover:bg-amber-100 text-amber-600',
      hoverColor: 'hover:shadow-amber-100',
      onClick: () => navigate('/html-scraper', { state: { isModalOpen: true } })
    },
    {
      label: 'Take Screenshot',
      icon: <CameraAlt className="w-5 h-5" />,
      color: 'bg-purple-50 hover:bg-purple-100 text-purple-600',
      hoverColor: 'hover:shadow-purple-100',
      onClick: () => navigate('/screenshots', { state: { isModalOpen: true } })
    },
    {
      label: 'Web Search',
      icon: <Search className="w-5 h-5" />,
      color: 'bg-indigo-50 hover:bg-indigo-100 text-indigo-600',
      hoverColor: 'hover:shadow-indigo-100',
      onClick: () => navigate('/web-search', { state: { isModalOpen: true } })
    },
    {
      label: 'Create Bio Page',
      icon: <Person className="w-5 h-5" />,
      color: 'bg-cyan-50 hover:bg-cyan-100 text-cyan-600',
      hoverColor: 'hover:shadow-cyan-100',
      onClick: () => navigate('/bio-pages', { state: { isModalOpen: true } })
    }
  ], [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/3 left-0 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 right-1/3 w-64 h-64 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                <Rocket className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Welcome Back, {userName}!</h1>
                <p className="text-gray-600">Here's your usage overview</p>
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-2 group"
            >
              <Add className="w-5 h-5 transition-transform group-hover:rotate-90" />
              <span>Create New</span>
            </button>
          </div>
        </motion.div>

        {/* Trial Warning */}
        <AnimatePresence>
          {(showTrial || showSubscriptionWarning) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 mb-6 border border-indigo-100 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center shadow">
                    <Timer className="text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {isSubscriptionEndingSoon ? 'Subscription Ending Soon' : 'Trial Ending Soon'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {isSubscriptionEndingSoon
                        ? `Your subscription ends on ${formatDateHumanReadable(subscriptionEndDate, 'date')}`
                        : `Your trial ends on ${formatDateHumanReadable(trialEndDate, 'date')}`
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/billings')}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                >
                  <Bolt className="w-4 h-4" />
                  Upgrade Now
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expired Trial */}
        <AnimatePresence>
          {isTrialExpired && subscriptionStatus === null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 mb-6 border border-red-100 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center shadow">
                    <Warning className="text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Trial Expired</h3>
                    <p className="text-sm text-gray-600">
                      Upgrade to continue using all features
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('billings')}
                  className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                >
                  <Bolt className="w-4 h-4" />
                  Upgrade Now
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* subscription ended */}
        <AnimatePresence>
          {subscriptionStatus == 'ended' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 mb-6 border border-red-100 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center shadow">
                    <Warning className="text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Subscription Expired</h3>
                    <p className="text-sm text-gray-600">
                      Upgrade to continue using all features
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('billings')}
                  className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                >
                  <Bolt className="w-4 h-4" />
                  Upgrade Now
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {cardData.map((card, index) => (
            <DashboardCard
              key={index}
              title={card.title}
              value={card.value}
              total={card.total}
              icon={card.icon}
              index={index}
            />
          ))}
        </div>

        {/* Modal */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              id="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={closeModal}
            >
              <motion.div
                className="bg-white mt-14 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-100"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Create New</h2>
                    <p className="text-gray-600 text-sm">Select an action to get started</p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                  >
                    <Close className="w-6 h-6" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {modalActions.map((action, index) => (
                    <motion.button
                      key={action.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -3, scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        action.onClick();
                        setIsModalOpen(false);
                      }}
                      className={`flex flex-col items-center justify-center gap-3 p-4 ${action.color} rounded-xl transition-all duration-200 hover:shadow-md ${action.hoverColor}`}
                    >
                      <div className="p-2.5 rounded-lg bg-white shadow-sm">
                        {action.icon}
                      </div>
                      <span className="font-medium text-sm">{action.label}</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DashboardHome;